import React, { useRef, useState } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../data/BaseUrl";
import { staff_off_boarding } from "../../../data/ApiEndpoints";
import ModalHeadAndClose from "../../payouts-wtf/common/ModalHeadAndClose";
import DateRanger from "../../components-react/DateRanger";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import StaffCalendarViewReport from "../../trainer-report/staff-attendance-report/new-ui/StaffCalendarViewReport";
import "../../../styles/staff-onboarding/staff-offboarding.css";

/**
 * Staff Offboarding wizard (Full & Final). Ported from main-dash (same UI/UX),
 * adapted for powered: mobile-first submit to POST /staff-offboarding/add
 * (backend deactivates the staff txn-safely), attendance via /staff/report/logs.
 */

const STEPS = { VALIDATE: 1, ATTENDANCE: 2, CONFIRM: 3 };

const StaffOffboarding = ({ userInfoData, rowData, closeModal, callFunction }) => {
  const [step, setStep] = useState(STEPS.VALIDATE);
  const [maxStepReached, setMaxStepReached] = useState(STEPS.VALIDATE);
  // Start empty so the user explicitly picks the F&F period (matches main-dash).
  const [fnfInitiateDate, setFnfInitiateDate] = useState("");
  const [fnfDoneDate, setFnfDoneDate] = useState("");
  const [showDateRanger, setShowDateRanger] = useState(false);
  const [rangeState, setRangeState] = useState([
    {
      startDate: moment().subtract(30, "days").toDate(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  const staffUserId = rowData?.staff_id || rowData?.user_id || "";

  const handleDateRangeChange = (item) => {
    const { startDate, endDate } = item.selection;
    setRangeState([item.selection]);
    setFnfInitiateDate(moment(startDate).format("YYYY-MM-DD"));
    setFnfDoneDate(moment(endDate).format("YYYY-MM-DD"));
    setAttendanceData(null);
    setAttendanceStats(null);
    setMaxStepReached(STEPS.VALIDATE);
  };

  const clearDateRange = () => {
    setFnfInitiateDate("");
    setFnfDoneDate("");
    setRangeState([
      {
        startDate: moment().subtract(30, "days").toDate(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    setAttendanceData(null);
    setAttendanceStats(null);
    setMaxStepReached(STEPS.VALIDATE);
  };

  const validateDateRange = () => {
    if (!fnfInitiateDate || !fnfDoneDate) {
      toast.error("Please select both the start and end of the attendance period");
      return false;
    }
    const today = moment().endOf("day");
    if (moment(fnfInitiateDate).isAfter(today) || moment(fnfDoneDate).isAfter(today)) {
      toast.error("Future dates are not allowed");
      return false;
    }
    if (moment(fnfDoneDate).isBefore(moment(fnfInitiateDate))) {
      toast.error("End date cannot be before start date");
      return false;
    }
    return true;
  };

  const handleValidateAndFetch = async () => {
    if (!validateDateRange()) return;
    setLoading(true);
    try {
      let data = [];
      let stats = null;
      if (staffUserId) {
        const url = `${BASE_URL}/staff/report/logs?user_id=${encodeURIComponent(
          staffUserId
        )}&date_from=${fnfInitiateDate}&date_to=${fnfDoneDate}`;
        const res = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${userInfoData?.token}` },
        });
        const json = await res.json();
        data = json?.data || json?.allData || [];
        stats = json?.stats || null;
      }
      setAttendanceData(data);
      setAttendanceStats(stats);
      setStep(STEPS.ATTENDANCE);
      setMaxStepReached((prev) => Math.max(prev, STEPS.ATTENDANCE));
    } catch (err) {
      console.error("offboard attendance fetch=>", err);
      setAttendanceData([]);
      setStep(STEPS.ATTENDANCE);
      setMaxStepReached((prev) => Math.max(prev, STEPS.ATTENDANCE));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      const payload = {
        mobile: rowData?.mobile || null,
        user_id: staffUserId || null,
        // Same as main-dash: start of period = FNF Initiate Date,
        // end of period = Last Working Date.
        fnf_initiate_date: fnfInitiateDate,
        fnf_done_date: fnfDoneDate || null,
        fnf_status: "pending",
        attendance: attendanceData || {},
        offboarded_by: userInfoData?.uid || null,
        offboarded_done_by: null,
      };
      const res = await fetch(`${BASE_URL}${staff_off_boarding}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfoData?.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json?.status) {
        toast.success(json?.message || "Staff off-boarding initiated successfully!");
        if (callFunction) callFunction();
        if (closeModal) closeModal();
      } else {
        toast.error(json?.message || "Failed to initiate off-boarding");
      }
    } catch (err) {
      console.error("offboard submit=>", err);
      toast.error("Failed to initiate off-boarding. Check your connection and try again.");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const confirmSubmit = () => {
    showConfirmAlert(
      handleSubmit,
      "This immediately deactivates the staff member across the system and opens their FnF settlement. It can't be undone from here.",
      `Offboard ${rowData?.name || "this employee"}?`,
      "Yes, offboard"
    );
  };

  const renderStepIndicator = () => (
    <div className="staff-offboarding-steps">
      {["Validate", "Attendance", "Confirm"].map((label, i) => {
        const stepNum = i + 1;
        const canNavigate = stepNum <= maxStepReached;
        return (
          <div
            key={label}
            className={`staff-offboarding-step ${step >= stepNum ? "active" : ""} ${
              step > stepNum ? "completed" : ""
            } ${canNavigate ? "clickable" : ""}`}
            onClick={() => canNavigate && setStep(stepNum)}
            role={canNavigate ? "button" : undefined}
            tabIndex={canNavigate ? 0 : undefined}
            aria-current={step === stepNum ? "step" : undefined}
          >
            <span className="staff-offboarding-step-number">
              {step > stepNum ? "✓" : stepNum}
            </span>
            <span className="staff-offboarding-step-label">{label}</span>
          </div>
        );
      })}
    </div>
  );

  const renderValidateStep = () => (
    <>
      <h3 className="staff-offboarding-section-title">Validate Employee</h3>

      <div className="staff-offboarding-info">
        <div className="staff-offboarding-info-header">
          <div className="staff-offboarding-avatar">
            {(rowData?.name || "N").charAt(0).toUpperCase()}
          </div>
          <div className="staff-offboarding-info-main">
            <p className="staff-offboarding-name">{rowData?.name || "NA"}</p>
            <span className="staff-offboarding-role-badge">
              {rowData?.account_type?.replace(/_/g, " ") || "-"}
            </span>
          </div>
        </div>

        <div className="staff-offboarding-info-divider"></div>

        <div className="staff-offboarding-info-details">
          {(rowData?.gym_name || rowData?.gym) && (
            <div className="staff-offboarding-info-row">
              <span className="staff-offboarding-info-label">Gym</span>
              <span className="staff-offboarding-info-value">
                {rowData.gym_name || rowData.gym}
              </span>
            </div>
          )}
          {(rowData?.mobile || rowData?.phone) && (
            <div className="staff-offboarding-info-row">
              <span className="staff-offboarding-info-label">Mobile</span>
              <span className="staff-offboarding-info-value">
                {rowData.mobile || rowData.phone}
              </span>
            </div>
          )}
          {rowData?.email && (
            <div className="staff-offboarding-info-row">
              <span className="staff-offboarding-info-label">Email</span>
              <span className="staff-offboarding-info-value text-lowercase">
                {rowData.email}
              </span>
            </div>
          )}
          {staffUserId && (
            <div className="staff-offboarding-info-row">
              <span className="staff-offboarding-info-label">Staff ID</span>
              <span className="staff-offboarding-info-value">{staffUserId}</span>
            </div>
          )}
        </div>
      </div>

      <div className="staff-offboarding-date-row">
        <div className="staff-offboarding-field">
          <label>Attendance Period</label>
          <div
            className="staff-offboarding-date-input"
            onClick={() => setShowDateRanger(true)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {fnfInitiateDate ? (
              <span className="staff-offboarding-date-range-display">
                <span className="staff-offboarding-date-chip">
                  {moment(fnfInitiateDate).format("DD MMM YYYY")}
                </span>
                <span className="staff-offboarding-date-separator">—</span>
                <span className="staff-offboarding-date-chip">
                  {fnfDoneDate ? moment(fnfDoneDate).format("DD MMM YYYY") : "Select"}
                </span>
              </span>
            ) : (
              <span style={{ color: "#999" }}>Select date range</span>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="#666"
              viewBox="0 0 16 16"
            >
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z" />
            </svg>
          </div>
        </div>
      </div>

      {showDateRanger && (
        <DateRanger
          handleDateRangeChange={handleDateRangeChange}
          rangeState={rangeState}
          closeRanger={() => setShowDateRanger(false)}
          onClose={() => setShowDateRanger(false)}
          onClear={clearDateRange}
          isDisableFutureDates={true}
        />
      )}

      <button
        type="button"
        className="staff-offboarding-submit-btn"
        disabled={loading || !fnfInitiateDate}
        onClick={handleValidateAndFetch}
      >
        {loading ? "Fetching Attendance..." : "Next — Fetch Attendance"}
      </button>
    </>
  );

  const renderAttendanceStep = () => (
    <>
      <div className="staff-offboarding-attendance-header">
        <h3 className="staff-offboarding-section-title">Attendance Snapshot</h3>
        <div className="staff-offboarding-btn-group">
          <button
            type="button"
            className="staff-offboarding-back-btn"
            onClick={() => setStep(STEPS.VALIDATE)}
          >
            Back
          </button>
          <button
            type="button"
            className="staff-offboarding-submit-btn"
            onClick={() => {
              setStep(STEPS.CONFIRM);
              setMaxStepReached((prev) => Math.max(prev, STEPS.CONFIRM));
            }}
          >
            Next — Review &amp; Confirm
          </button>
        </div>
      </div>

      <div className="staff-offboarding-attendance">
        {staffUserId ? (
          <StaffCalendarViewReport
            userInfoData={userInfoData}
            rowData={{ user_id: staffUserId }}
          />
        ) : (
          <p className="info">Attendance not available for this staff.</p>
        )}
      </div>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <h3 className="staff-offboarding-section-title">Confirm Offboarding</h3>

      <div className="staff-offboarding-summary">
        <div className="staff-offboarding-summary-row">
          <span>Employee</span>
          <strong>{rowData?.name || "NA"}</strong>
        </div>
        <div className="staff-offboarding-summary-row">
          <span>Role</span>
          <strong>{rowData?.account_type?.replace(/_/g, " ") || "-"}</strong>
        </div>
        <div className="staff-offboarding-summary-row">
          <span>Period Start Date</span>
          <strong>
            {fnfInitiateDate ? moment(fnfInitiateDate).format("DD MMM YYYY") : "-"}
          </strong>
        </div>
        <div className="staff-offboarding-summary-row">
          <span>Last Working Date</span>
          <strong>
            {fnfDoneDate ? moment(fnfDoneDate).format("DD MMM YYYY") : "-"}
          </strong>
        </div>
        <div className="staff-offboarding-summary-row">
          <span>Attendance Records</span>
          <strong>
            {Array.isArray(attendanceData) ? attendanceData.length : 0} entries saved
          </strong>
        </div>
        <div className="staff-offboarding-summary-row">
          <span>Offboarded By</span>
          <strong>{userInfoData?.name || userInfoData?.uid || "-"}</strong>
        </div>
      </div>

      <p className="staff-offboarding-warning">
        This will mark the employee as offboarded and lock their attendance records.
      </p>

      <div className="staff-offboarding-btn-group">
        <button
          type="button"
          className="staff-offboarding-back-btn"
          onClick={() => setStep(STEPS.ATTENDANCE)}
        >
          Back
        </button>
        <button
          type="button"
          className="staff-offboarding-confirm-btn"
          disabled={loading}
          onClick={confirmSubmit}
        >
          {loading ? "Processing..." : "Confirm Offboarding"}
        </button>
      </div>
    </>
  );

  return (
    <div className="staff-offboarding-container">
      <ModalHeadAndClose title="Staff Offboarding" onClose={closeModal} />

      <div className="staff-offboarding-content">
        {renderStepIndicator()}

        <div className="staff-offboarding-form">
          {step === STEPS.VALIDATE && renderValidateStep()}
          {step === STEPS.ATTENDANCE && renderAttendanceStep()}
          {step === STEPS.CONFIRM && renderConfirmStep()}
        </div>
      </div>
    </div>
  );
};

export default StaffOffboarding;
